import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Crown, Mail, Lock, Eye, EyeOff, AlertCircle, User, Check, Calendar } from 'lucide-react';
import { api } from '../lib/api';
import { toast } from 'sonner';

// VIP signup is hidden; the tier selector is hidden with it.
const SHOW_TIER_SELECTOR = false;

// Full years between a YYYY-MM-DD birth date and `today` (local calendar date).
// Returns null for an unparseable or future date. (members-hotfix: the old inline
// version reassigned a const and crashed before the birthday in the current year.)
export function ageOnDate(birthDateStr, today = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(birthDateStr || '').trim());
  if (!m) return null;
  const year = Number(m[1]);
  const month = Number(m[2]) - 1;
  const day = Number(m[3]);
  const check = new Date(year, month, day);
  if (check.getFullYear() !== year || check.getMonth() !== month || check.getDate() !== day) return null;
  let age = today.getFullYear() - year;
  const monthDiff = today.getMonth() - month;
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < day)) {
    age -= 1;
  }
  return age < 0 ? null : age;
}

export default function MemberRegister() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    birthDate: '',
    tier: 'member'
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      setError('Name is required');
      return false;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return false;
    }
    if (!formData.birthDate) {
      setError('Birth date is required');
      return false;
    }
    // Validate age (must be 18+)
    const age = ageOnDate(formData.birthDate);
    if (age === null) {
      setError('Please enter a valid birth date');
      return false;
    }
    if (age < 18) {
      setError('You must be at least 18 years old to register');
      return false;
    }
    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;

    setLoading(true);
    try {
      const response = await api.post('/api/members/auth/register', {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        birth_date: formData.birthDate
      });

      localStorage.setItem('memberUser', JSON.stringify(response.data.user));
      localStorage.setItem('memberToken', response.data.token);

      toast.success('Account created successfully! 🎉');
      
      // Redirect to free members area
      navigate('/members/free');
    } catch (err) {
      console.error('Register error:', err);
      const errorMsg = err.response?.data?.detail || 'Registration failed';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-950 via-purple-950/20 to-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo/Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full mb-4">
            <Crown size={40} className="text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2">Join the Club</h1>
          <p className="text-zinc-400">Create your account for exclusive content</p>
        </div>

        {/* Register Form */}
        <div className="bg-zinc-900/50 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleRegister} className="space-y-5">
            {/* Error Alert */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm">{error}</p>
              </div>
            )}

            {SHOW_TIER_SELECTOR && (
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-3">
                Membership Tier
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, tier: 'member' })}
                  className={`p-4 rounded-lg border-2 transition ${
                    formData.tier === 'member'
                      ? 'border-purple-500 bg-purple-500/10'
                      : 'border-white/10 bg-zinc-800/50 hover:border-white/20'
                  }`}>
                  <div className="flex items-center justify-between mb-2">
                    <User size={20} className="text-purple-400" />
                    {formData.tier === 'member' && (
                      <Check size={16} className="text-purple-400" />
                    )}
                  </div>
                  <div className="text-white font-semibold">Member</div>
                  <div className="text-xs text-zinc-500 mt-1">Standard access</div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, tier: 'vip' })}
                  className={`p-4 rounded-lg border-2 transition ${
                    formData.tier === 'vip'
                      ? 'border-yellow-500 bg-yellow-500/10'
                      : 'border-white/10 bg-zinc-800/50 hover:border-white/20'
                  }`}>
                  <div className="flex items-center justify-between mb-2">
                    <Crown size={20} className="text-yellow-400" />
                    {formData.tier === 'vip' && (
                      <Check size={16} className="text-yellow-400" />
                    )}
                  </div>
                  <div className="text-white font-semibold">VIP</div>
                  <div className="text-xs text-zinc-500 mt-1">Premium access</div>
                </button>
              </div>
            </div>
            )}

            {/* Name */}
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">
                Full Name
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 transition"
                  placeholder="John Doe"
                />
              </div>
            </div>

            {/* Birth Date */}
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">
                Date of Birth
              </label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input
                  type="date"
                  name="birthDate"
                  value={formData.birthDate}
                  onChange={handleChange}
                  required
                  max={new Date(new Date().setFullYear(new Date().getFullYear() - 18)).toISOString().split('T')[0]}
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 transition [color-scheme:dark]"
                />
              </div>
              <p className="text-xs text-zinc-500 mt-1">You must be 18 or older to register</p>
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">
                Email Address
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 transition"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg pl-11 pr-12 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 transition"
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition">
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              <p className="text-xs text-zinc-500 mt-1">At least 6 characters</p>
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-semibold text-zinc-300 mb-2">
                Confirm Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={20} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  className="w-full bg-zinc-800/50 border border-white/10 rounded-lg pl-11 pr-4 py-3 text-white placeholder-zinc-500 focus:outline-none focus:border-purple-500 transition"
                  placeholder="••••••••"
                />
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold py-3 rounded-lg transition flex items-center justify-center gap-2">
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Creating Account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-4 bg-zinc-900/50 text-zinc-500">Already have an account?</span>
            </div>
          </div>

          {/* Login Link */}
          <Link
            to="/members/login"
            className="block w-full bg-zinc-800/50 hover:bg-zinc-800 border border-white/10 text-white font-semibold py-3 rounded-lg transition text-center">
            Sign In
          </Link>

          {/* Back to Home */}
          <div className="mt-6 text-center">
            <Link to="/" className="text-sm text-zinc-500 hover:text-white transition">
              ← Back to Home
            </Link>
          </div>
        </div>

        {/* Footer Note */}
        <p className="text-center text-zinc-600 text-xs mt-6">
          By creating an account, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
}
